
import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import {
    ActivityIndicator,
    FlatList,
    Image,
    ImageBackground,
    TouchableOpacity,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import EventTimer from "./components/eventTimer";
import {
    BottomSheet,
    Button,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    RowCard,
    Text,
    type IBottomSheetRef
} from "ncore-ui-kit";
import {
    useGlobalState
} from "../../../context";
import {
    useDebouncedDimensions
} from "../../../utils";
import {
    useNavigation
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    ActiveEventsWithoutAuthObjectType as ActiveEventsObjectType,
    IncomingEventsWithoutAuthObjectType as IncomingEventsObjectType,
    OtherEventsWithoutAuthObjectType as OtherEventsObjectType
} from "../../../services/restAPI/actions/getEventsWithoutAuth/types";
import type GuestStackParamList from "../../../types/navigation/guest";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronRightIcon,
    QrCode
} from "lucide-react-native";
import moment from "moment";
import {
    FooterVersionInfo,
    UserCard
} from "../../../components";
import {
    RESTService
} from "../../../services/restAPI";
import {
    menuItems
} from "../../app/home/constants";

const Explore = () => {
    const {
        windowWidth
    } = useDebouncedDimensions();

    const navigation = useNavigation<NativeStackNavigationProp<GuestStackParamList>>();
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [globalState] = useGlobalState();

    const [loading,
        setLoading] = useState(true);
    const [activeEventData,
        setActiveEventData] = useState<Array<ActiveEventsObjectType>>([]);
    const [incomingEventsData,
        setIncomingEventsData] = useState<Array<IncomingEventsObjectType>>([]);
    const [otherEventsData,
        setOtherEventsData] = useState<Array<OtherEventsObjectType>>([]);

    const inset = useSafeAreaInsets();

    const menuRef = useRef<IBottomSheetRef>(null);

    useEffect(() => {
        getEventsData();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => renderHeader()
        });
    }, [navigation]);

    const getEventsData = () => {
        RESTService.action("GetEventsWithoutAuth", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setActiveEventData(res.payload.activeEvents);
                    setIncomingEventsData(res.payload.nextEvents);
                    setOtherEventsData(res.payload.otherEvents);
                    setLoading(false);
                }
            }).catch((err) => {
                setLoading(false);
                console.log(err);
            });
    };

    const renderHeader = () => {
        return <View
            style={{
                ...stylesheet.headerContent,
                paddingTop:inset.top + spaces.spacingMd,
                paddingHorizontal: spaces.spacingMd,
                paddingBottom: spaces.spacingMd
            }}
        >
            <UserCard
                fullName={globalState.isGuest ? "Misafir" : globalState.user?.fullName || ""}
                profilePhotoURL={globalState.user?.profilePhotoURL}
                company="Genç MÜSİAD - Konya"
                onPress={() => {
                    navigation.navigate("Profile");
                }}
            />

        </View>;
    };

    const renderActiveEvents = () => {
        if(!activeEventData || !activeEventData.length) {
            return null;
        }

        return <View
            style={{
                marginBottom: spaces.spacingLg
            }}
        >
            <Text
                variant="bodyMediumSize"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {localize("activeEvent")}
            </Text>
            <FlatList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                key={`activeEvents-${windowWidth}`}
                keyExtractor={(item) => item.eventID}
                renderItem={({
                    item
                }) => {
                    return <ImageBackground
                        source={{
                            uri: item.coverPhoto
                        }}
                        resizeMode="cover"
                        style={[
                            stylesheet.eventCard,
                            {
                                borderRadius: radiuses.xl,
                                backgroundColor: colors.content.container.info,
                                paddingHorizontal: spaces.spacingLg,
                                paddingVertical: spaces.spacingMd,
                                width: windowWidth - spaces.spacingXl,
                                marginRight: activeEventData.length > 1 ? spaces.spacingMd : 0
                            }
                        ]}
                    >
                        {
                            item.eventStatus === "continue" ?<View
                                style={[
                                    stylesheet.eventStatusBadge,
                                    {
                                        paddingHorizontal: spaces.spacingMd,
                                        paddingVertical: spaces.spacingSm,
                                        backgroundColor: colors.content.container.danger,
                                        borderRadius: radiuses.xl,
                                        marginBottom: spaces.spacingMd
                                    }
                                ]}
                            >
                                <View
                                    style={{
                                        width: spaces.spacingMd,
                                        height: spaces.spacingMd,
                                        borderRadius: radiuses.full,
                                        backgroundColor: colors.content.icon.danger,
                                        marginRight: spaces.spacingSm
                                    }}
                                />
                                <Text
                                    variant="bodyLargeSize"
                                    color="danger"
                                >
                                    {localize("active2")}
                                </Text>
                            </View> : null
                        }
                        <Text
                            numberOfLines={3}
                            variant="headlineLargeSize"
                            textBreakStrategy="highQuality"
                            color="onPrimary"
                            style={[
                                stylesheet.eventTitle,
                                {
                                    marginBottom: spaces.spacingSm
                                }
                            ]}
                        >
                            {item.eventTitle}
                        </Text>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    marginBottom: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text
                                style={{
                                    marginRight: spaces.spacingXs
                                }}
                                color="onPrimary"
                            >
                                {localize("today")}
                            </Text>
                            <Text
                                style={{
                                    marginRight: spaces.spacingXs
                                }}
                                color="onPrimary"
                            >
                                •
                            </Text>
                            <Text
                                color="onPrimary"
                            >
                                {item.eventLocation}
                            </Text>
                        </View>
                        {
                            item.eventType === "join" ? <Button
                                title={localize("join")}
                                onPress={() => {
                                    navigation.navigate("ScanQRGuest", {
                                        eventID: item.eventID,
                                        eventSubID: item.eventSubID
                                    });
                                }}
                                icon={() => <QrCode
                                    color={colors.content.container.default}
                                    size={20}
                                />}
                                style={{
                                    marginBottom: spaces.spacingLg
                                }}
                                spreadBehaviour="baseline"
                                customColor={colors.content.icon.danger}
                                customBorderColor={colors.content.icon.danger}
                                customTextColor={colors.content.container.default}
                                size="medium"
                            /> : null
                        }
                    </ImageBackground>;
                }}
                horizontal={true}
                data={activeEventData}
            />
        </View>;
    };

    const renderIncomingEvents = () => {
        if(!incomingEventsData || !incomingEventsData.length) {
            return null;
        }

        return <View
            style={{
                marginBottom: spaces.spacingMd
            }}
        >
            <Text
                variant="bodyMediumSize"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {localize("nextEvent")}
            </Text>
            <FlatList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                keyExtractor={(item) => item.eventID}
                horizontal={true}
                data={incomingEventsData}
                renderItem={({
                    item
                }) => {
                    return <View
                        style={[
                            stylesheet.flex1,
                            {
                                borderRadius: radiuses.xl,
                                backgroundColor: colors.content.container.mid,
                                paddingHorizontal: spaces.spacingLg,
                                paddingVertical: spaces.spacingMd,
                                marginRight: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            variant="titleMediumSize"
                            style={{
                                marginBottom: spaces.spacingSm
                            }}
                        >
                            {item?.eventTitle}
                        </Text>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    marginBottom: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text
                                style={{
                                    marginRight: spaces.spacingXs
                                }}
                            >
                                {moment(item?.eventStartDate).format("DD MMMM YYYY")}
                            </Text>
                            <Text
                                style={{
                                    marginRight: spaces.spacingXs
                                }}
                            >
                                •
                            </Text>
                            <Text
                            >
                                {item.eventLocation}
                            </Text>
                        </View>
                        <EventTimer
                            startTime={item?.eventStartDate}
                        />
                        {
                            item.eventType === "join" && item.eventSubType === "application" ? <Button
                                title={localize("fillApplicationForm")}
                                type="primary"
                                onPress={() =>{
                                    navigation.navigate("GuestApplyForm", {
                                        eventID: item.eventID,
                                        eventSubID: item.eventSubID,
                                        formID: item.formID
                                    });
                                }}
                            />: null
                        }
                    </View>;
                }}
            />
        </View>;
    };

    const renderOtherEvents = () => {
        return  <View
            style={{
                marginBottom: spaces.spacingXl * 4
            }}
        >
            <Text
                variant="bodyMediumSize"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {localize("otherEvents")}
            </Text>
            <FlatList
                showsVerticalScrollIndicator={false}
                keyExtractor={(item) => item.eventID}
                renderItem={({
                    item
                }) => {
                    return <TouchableOpacity
                        onPress={() => {
                            navigation.navigate("GuestEventDetail", {
                                eventID: item.eventID
                            });
                        }}
                        style={[
                            stylesheet.otherEventCard,
                            {
                                borderRadius: radiuses.xl,
                                backgroundColor: colors.content.container.info,
                                marginBottom: spaces.spacingMd
                            }
                        ]}
                    >
                        <Image
                            source={{
                                uri: item.coverPhoto
                            }}
                            resizeMethod="resize"
                            resizeMode="cover"
                            style={[
                                stylesheet.coverImage,
                                {
                                    borderRadius: radiuses.xl
                                }
                            ]}
                        />
                        <View
                            style={{
                                ...stylesheet.otherEventsContent,
                                backgroundColor: colors.content.container.mid,
                                paddingHorizontal: spaces.spacingMd,
                                paddingVertical: spaces.spacingMd,
                                borderBottomLeftRadius: radiuses.xl,
                                borderBottomRightRadius: radiuses.xl
                            }}
                        >
                            <View
                                style={stylesheet.otherEventInfoRow}
                            >
                                <Image
                                    source={{
                                        uri: item.logo
                                    }}
                                    resizeMethod="resize"
                                    resizeMode="contain"
                                    style={[
                                        stylesheet.otherEventLogo,
                                        {
                                            marginRight: spaces.spacingMd
                                        }
                                    ]}
                                />
                                <View
                                    style={stylesheet.otherEventTitleContainer}
                                >
                                    <Text
                                        variant="titleMediumSize"
                                        numberOfLines={4}
                                        style={stylesheet.otherEventTitle}
                                    >
                                        {item?.eventTitle}
                                    </Text>
                                    <Text
                                        color="mid"
                                    >
                                        {localize("event")}
                                    </Text>
                                </View>
                            </View>
                            <View
                                style={[
                                    stylesheet.otherEventCount,
                                    {
                                        borderRadius: radiuses.xl,
                                        paddingHorizontal: spaces.spacingLg,
                                        paddingVertical: spaces.spacingSm,
                                        backgroundColor: colors.project.eventCount,
                                        right: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    variant="bodyLargeSize"
                                >
                                    {localize("eventCount", [item?.eventLength])}
                                </Text>
                            </View>
                        </View>
                    </TouchableOpacity>;
                }}
                horizontal={false}
                data={otherEventsData}
            />
        </View>;
    };

    const renderMenuSheet= () => {
        return <BottomSheet
            ref={menuRef}
            renderHeader={() => {
                return <View
                    style={[
                        stylesheet.menuHeader,
                        {
                            padding: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        variant="headlineSmallSize"
                        color="high"
                    >
                        Genç MÜSİAD
                    </Text>
                </View>;
            }}
            isCanFullScreenOnSwipe={false}
            style={{
                borderTopRightRadius: radiuses.xl,
                borderTopLeftRadius: radiuses.xl
            }}
        >
            {menuItems.map((item) => <RowCard
                titleVariant="titleMediumSize"
                key={item.id}
                title={localize(item.titleKey)}
                isTransparentBackground={true}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                icon={() => {
                    const IconComponent = item.icon;
                    return <View
                        style={{
                            marginRight: spaces.spacingMd
                        }}
                    >
                        <IconComponent
                            color={colors.content.icon.onPrimary}
                            size={24}
                        />
                    </View>;
                }}
                rightIcon={() => <ChevronRightIcon
                    color={colors.content.icon.high}
                />
                }
                onPress={() => {
                    if (item.routeName) {
                        menuRef.current?.close();
                        navigation.navigate(item.routeName as never);
                    }
                }}
            />)}
            <FooterVersionInfo
                isProfile= {false}
            />
        </BottomSheet>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={true}
        backgroundColor="subtle"
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderActiveEvents()}
        {renderIncomingEvents()}
        {renderOtherEvents()}
        {renderMenuSheet()}
    </PageContainer>;
};
export default Explore;
